import { Grid } from "@mui/material";
import { useRecoilState } from "recoil";
import { useState, useEffect } from "react";
import { Info } from "@mui/icons-material";
import {
  NewRequisitionTextField,
  NewReqStateGrid,
  NewRequisitionTextFieldLabel,
  NewReqStreetGrid,
  NewReqEmailHelper,
  handleKeyDown,
} from "./NewRequisition.styles";
import { currentNewRequisitionEdit } from "../../recoil/atoms";
import FormField from "../shared/TextField/FormField";
import { statesArray } from "./types";
import { ExclamationIcon } from "../../assets/iconComponents/ExclamationIcon";
import {
  FormatedAddress,
  GetGooglePlacesAddress,
  GetGooglePlacesAddressDetails,
} from "./Services/NewReqService";
import { checkPhoneAndEmail } from "./helpers";
import { useAuthenticationHook } from "../../utils/hooks/useAuthenticationHook";

const PatientInformationAddress: React.FC<{ disabled?: boolean }> = ({
  disabled = false,
}) => {
  const [newRequisition, setNewRequisition] = useRecoilState(
    currentNewRequisitionEdit,
  );
  const [checkPhoneError, setCheckPhoneError] = useState(false);
  const [checkEmailError, setCheckEmailError] = useState(false);

  const { requisitionForm } = newRequisition;
  const [autocompleteAddressList, setAutocompleteAddressList] = useState<
    FormatedAddress[]
  >([]);
  const [selectedAutoCompleteAddress, setSelectedAutoCompleteAddress] =
    useState<FormatedAddress>();
  const { auth, setLoggedOut } = useAuthenticationHook();
  useEffect(() => {
    const getAutocompleteAddresses = async () => {
      if (!disabled && requisitionForm.streetAddress.length && auth) {
        const addresses = await GetGooglePlacesAddress(
          requisitionForm.streetAddress,
          auth.token,
          setLoggedOut,
        );
        setAutocompleteAddressList(addresses);
      }
    };
    getAutocompleteAddresses();
  }, [requisitionForm.streetAddress]);

  useEffect(() => {
    const getAutocompleteAddressesDetails = async () => {
      if (selectedAutoCompleteAddress && auth) {
        const details = await GetGooglePlacesAddressDetails(
          selectedAutoCompleteAddress,
          auth.token,
          setLoggedOut,
        );

        if (details) {
          setNewRequisition({
            ...newRequisition,
            requisitionForm: {
              ...newRequisition.requisitionForm,
              fullAddress: details.fullAddress || "",
              streetAddress:
                `${details.houseNumber} ${details.streetName}` || "",
              state:
                statesArray.find((state) =>
                  state.split("-")[0].includes(details.state),
                ) || details.state,
              city: details.city || "",
              zipCode: details.zip || "",
            },
          });
          setSelectedAutoCompleteAddress(undefined);
          setAutocompleteAddressList([]);
        }
      }
    };
    getAutocompleteAddressesDetails();
  }, [selectedAutoCompleteAddress]);
  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setNewRequisition({
      ...newRequisition,
      requisitionForm: {
        ...newRequisition.requisitionForm,
        [e.target.name]: e.target.value,
      },
    });
  };
  const isEmailRequired = requisitionForm.billToPatient;
  const hasEmailError =
    !disabled &&
    (newRequisition.isError || checkEmailError) &&
    ((isEmailRequired && !requisitionForm.emailAddress) ||
      checkPhoneAndEmail("email", requisitionForm));

  const hasCheckedPhone =
    !disabled && (newRequisition.isError || checkPhoneError);

  return (
    <>
      <Grid
        container
        spacing={3}
        sx={{ py: 2, pt: 3 }}
        columns={{ xs: 4, sm: 10, md: 12 }}
      >
        <Grid item md={6} sm={5} xs={4}>
          <NewRequisitionTextField
            disabled={disabled}
            label={
              <NewRequisitionTextFieldLabel>
                {`Cell Phone${
                  requisitionForm.isInternationalAccount ? "" : "*"
                }`}
                {hasCheckedPhone &&
                  checkPhoneAndEmail("phone", requisitionForm) && (
                    <ExclamationIcon />
                  )}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-cell-phone"
            variant="standard"
            size="small"
            placeholder="Cell Phone"
            fullWidth
            focused
            onKeyDown={handleKeyDown}
            error={
              hasCheckedPhone && checkPhoneAndEmail("phone", requisitionForm)
                ? "Please enter a valid phone number."
                : ""
            }
            $showHelpertext={
              Boolean(requisitionForm.cellPhone) &&
              checkPhoneAndEmail("phone", requisitionForm)
            }
            value={requisitionForm.cellPhone}
            name="cellPhone"
            onChange={onChange}
            onBlur={() =>
              setCheckPhoneError(checkPhoneAndEmail("phone", requisitionForm))
            }
          />
        </Grid>

        <Grid item md={6} sm={5} xs={4}>
          <NewRequisitionTextField
            disabled={disabled}
            label={
              <NewRequisitionTextFieldLabel>
                Email Address{isEmailRequired ? "*" : ""}
                {hasEmailError && <ExclamationIcon />}
              </NewRequisitionTextFieldLabel>
            }
            autoComplete="nope"
            id="new-test-patient-email"
            variant="standard"
            size="small"
            placeholder="Email Address"
            fullWidth
            focused
            onKeyDown={handleKeyDown}
            error={hasEmailError ? "Please enter a valid email address." : ""}
            $showHelpertext
            value={requisitionForm.emailAddress}
            name="emailAddress"
            onChange={onChange}
            onBlur={() =>
              setCheckEmailError(checkPhoneAndEmail("email", requisitionForm))
            }
          />
          <NewReqEmailHelper>
            <Info /> We use this email address to inform patients about their
            results online.
          </NewReqEmailHelper>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={{ py: 2 }}
        columns={{ xs: 4, sm: 10, md: 12 }}
      >
        <NewReqStreetGrid $addPadding item md={8} sm={6} xs={4}>
          <FormField
            fieldTitle="Street Address"
            autoCompletePlaceholder="Street Address"
            fieldType="autocomplete"
            maxCharacters={255}
            newRequisition
            autoCompleteDropdownStyle
            autoCompleteOptions={autocompleteAddressList.map(
              (address) => address.fullAddress,
            )}
            fieldValue={requisitionForm.streetAddress}
            onFieldChange={(val) => {
              const selectedAddress = autocompleteAddressList.find(
                (address) => address.fullAddress === val,
              );
              if (selectedAddress) {
                setNewRequisition({
                  ...newRequisition,
                  requisitionForm: {
                    ...newRequisition.requisitionForm,
                    fullAddress: selectedAddress.fullAddress || "",
                    streetAddress:
                      `${selectedAddress.houseNumber} ${selectedAddress.streetName}` ||
                      "",
                  },
                });
                setSelectedAutoCompleteAddress(selectedAddress);
              } else {
                setNewRequisition({
                  ...newRequisition,
                  requisitionForm: {
                    ...newRequisition.requisitionForm,
                    streetAddress: val as string,
                  },
                });
              }
            }}
            edit={!disabled}
            autoCompleteFreeSolo
          />
        </NewReqStreetGrid>

        <Grid item md={4} sm={4} xs={4}>
          <NewRequisitionTextField
            disabled={disabled}
            id="new-test-patient-apt-suite"
            label="Apt / Unit / Suite"
            autoComplete="nope"
            variant="standard"
            size="small"
            placeholder="Apt / Unit / Suite"
            fullWidth
            focused
            onKeyDown={handleKeyDown}
            value={requisitionForm.aptUnit}
            name="aptUnit"
            onChange={onChange}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={{ py: 2 }}
        columns={{ xs: 4, sm: 9, md: 12 }}
      >
        <Grid item md={4} sm={3} xs={4}>
          <NewRequisitionTextField
            disabled={disabled}
            id="new-test-patient-city"
            label="City"
            autoComplete="nope"
            variant="standard"
            size="small"
            placeholder="City name"
            fullWidth
            focused
            onKeyDown={handleKeyDown}
            value={requisitionForm.city}
            name="city"
            onChange={onChange}
          />
        </Grid>

        <NewReqStateGrid item md={6} sm={4} xs={4}>
          <FormField
            fieldTitle="State"
            autoCompletePlaceholder="State"
            fieldType="autocomplete"
            newRequisition
            autoCompleteDropdownStyle
            autoCompleteOptions={statesArray}
            fieldValue={requisitionForm.state}
            onFieldChange={(val) => {
              setNewRequisition({
                ...newRequisition,
                requisitionForm: {
                  ...newRequisition.requisitionForm,
                  state: val as string,
                },
              });
            }}
            edit={!disabled}
          />
        </NewReqStateGrid>
        <Grid item md={2} sm={2} xs={4}>
          <NewRequisitionTextField
            disabled={disabled}
            id="new-test-patient-zip"
            label="Zip code"
            autoComplete="nope"
            variant="standard"
            size="small"
            placeholder="Zip code"
            fullWidth
            focused
            onKeyDown={handleKeyDown}
            value={requisitionForm.zipCode}
            name="zipCode"
            onChange={onChange}
          />
        </Grid>
      </Grid>
    </>
  );
};
export default PatientInformationAddress;
